/**
 * GTM Event Constants
 *
 * Event names, page type mappings, and timing constants for the GTM dataLayer
 * integration. Adapted from legacy breitling-storefront-next/src/lib/gtm/const.ts
 * for the multibrand App Router architecture.
 */

export const EVENT_NAME = {
  PAGE_VIEW: 'page_view',
  POPIN_VIEW: 'popin_view',
  POPIN_CLOSE: 'popin_close',
  NEWSLETTER_SUBSCRIPTION: 'newsletter_subscription',
  CTA_DISCOVER: 'cta_discover',
  VIDEO_START: 'video_start',
  VIDEO_PROGRESS: 'video_progress',
  VIDEO_COMPLETE: 'video_complete',
  VIEW_ITEM: 'view_item',
  VIEW_ITEM_LIST: 'view_item_list',
  SELECT_ITEM: 'select_item',
  ADD_TO_CART: 'add_to_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  INTENTION_VIEW_CART: 'intention_view_cart',
  VIEW_CART: 'view_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_SHIPPING_INFO: 'add_shipping_info',
  ADD_PAYMENT_INFO: 'add_payment_info',
  REVIEW_ORDER: 'review_order',
  PURCHASE: 'purchase',
  GUEST_CHECKOUT: 'guest_checkout',
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  SEARCH: 'search',
  CONTACT: 'contact',
  NOTIFY_ME: 'notify_me',
  AVAILABILITY_REQUEST_SUBMIT: 'availability_request_submit',
  AVAILABILITY_REQUEST: 'availability_request',
  INTENTION_AVAILABILITY_REQUEST: 'intention_availability_request',
  VIEW_PROMOTION: 'view_promotion',
  SELECT_PROMOTION: 'select_promotion',
  ADD_TO_WISHLIST: 'add_to_wishlist',
  REMOVE_FROM_WISHLIST: 'remove_from_wishlist',
  INTENTION_BOOK_APPT: 'intention_book_appt',
  INTENTION_REGISTER_WATCH: 'intention_register_watch',
  INTENTION_WARRANTY_EXTENSION: 'intention_warranty_extension',
  REGISTERED_WATCH: 'registered_watch',
  CALL_TO_BUY: 'call_to_buy',
  GENERATE_LEAD: 'generate_lead',
  BOOK_AN_APPT: 'book_an_appt',
  UG_LOCALE_SELECTED: 'ug_locale_selected',
  UG_FORM_SUBMIT: 'ug_form_submit',
} as const;

/** Breitling-aligned page_type values named in BEC-22976. */
export const PAGE_TYPE = {
  LOGIN_OR_ACCOUNT_CREATION: 'login or account creation interface',
  ACCOUNT_CREATION_FORM: 'account creation - form',
  MY_ACCOUNT_MY_WATCHES: 'my account - my watches',
} as const;

export type EventName = (typeof EVENT_NAME)[keyof typeof EVENT_NAME];

/**
 * Delay before firing page_view to ensure the URL has settled after navigation.
 * Reduced from legacy 500ms since we don't need auth resolution for June.
 */
export const GA_PAGE_VIEW_DELAY_MS = 300;

/**
 * Delay for ecommerce events (view_item, view_item_list) to ensure they fire after page_view.
 * Extra 100ms buffer ensures ecommerce events fire strictly after page_view.
 */
export const GA_ECOMMERCE_EVENT_DELAY_MS = GA_PAGE_VIEW_DELAY_MS + 100;

/**
 * Maps URL path segments to GTM menu categories.
 * The path is checked after stripping the locale prefix.
 */
export const menuCategoryMapping: Record<string, string> = {
  watches: 'watches',
  straps: 'straps',
  sunglasses: 'sunglasses',
  collections: 'watches',
  search: 'search',
  store: 'store',
  'my-account': 'my account',
  newsletter: 'newsletter',
  ecom: 'ecom',
  about: 'about',
  stories: 'stories',
  boutique: 'boutique',
  history: 'history',
  blog: 'blog',
  icons: 'icons',
};

/**
 * Maps URL paths/patterns to GTM page types.
 * Checked after stripping the locale prefix. First match wins.
 */
export const pageTypeMapping: Record<string, string> = {
  '/': 'homepage',
  '/watches/all/': 'watch product list page - all',
  '/straps/all/': 'strap product list page - all',
  '/straps/': 'strap product list page',
  '/sunglasses/all/': 'eyewear product list page - all',
  '/search/': 'search results',
  '/store/shopping-bag/': 'shopping bag',
  '/store/checkout/delivery/': 'checkout - shipping info',
  '/store/checkout/payment/': 'checkout - payment info',
  '/store/checkout/review/': 'checkout review',
  '/store/checkout/confirmation/': 'checkout - transaction confirmed',
  '/store/order/payment/': 'order payment',
  '/store/order/confirmation/': 'checkout - transaction confirmed',
  '/my-account/': 'my account',
  '/my-account/login/': PAGE_TYPE.LOGIN_OR_ACCOUNT_CREATION,
  '/my-account/register/': PAGE_TYPE.ACCOUNT_CREATION_FORM,
  '/my-account/my-watches/': PAGE_TYPE.MY_ACCOUNT_MY_WATCHES,
  '/my-account/my-details/': 'my details',
  '/my-account/my-wishlist/': 'my wishlist',
  '/my-account/addresses/': 'my addresses',
  '/newsletter/subscribe/confirm/': 'newsletter confirmation',
};

/**
 * Regex-based page type matching for dynamic routes.
 * Evaluated in order after static pageTypeMapping fails.
 */
export const regExpMapping: Array<{ pattern: RegExp; pageType: string }> = [
  { pattern: /^\/watches\/[^/]+\/[^/]+\/[^/]+\/$/, pageType: 'watch product detail page' },
  { pattern: /^\/watches\/[^/]+\/$/, pageType: 'watch product list page' },
  { pattern: /^\/straps\/[^/]+\/[^/]+\/$/, pageType: 'strap product detail page' },
  { pattern: /^\/straps\/[^/]+\/$/, pageType: 'strap product list page' },
  { pattern: /^\/sunglasses\/[^/]+\/$/, pageType: 'eyewear product detail page' },
  { pattern: /^\/collections\//, pageType: 'watch product list page - collections' },
  { pattern: /^\/about\//, pageType: 'about' },
  { pattern: /^\/my-account\//, pageType: 'my account' },
  { pattern: /^\/store\/order\/confirmation\//, pageType: 'checkout - transaction confirmed' },
  { pattern: /^\/store\/checkout\//, pageType: 'checkout' },
];

/**
 * Country-to-ecommerce-market mapping.
 * "yes" = full ecommerce available, "no" = catalog-only.
 */
export const marketEcomCountries: Record<string, 'yes' | 'no'> = {
  US: 'yes',
  GB: 'yes',
  DE: 'yes',
  FR: 'yes',
  CH: 'yes',
  AT: 'yes',
  IT: 'yes',
  ES: 'yes',
  NL: 'yes',
  BE: 'yes',
  JP: 'no',
  AU: 'no',
  CA: 'yes',
  SG: 'yes',
  HK: 'yes',
  TW: 'yes',
  KR: 'yes',
  CN: 'no',
  IN: 'no',
  BR: 'no',
  MX: 'no',
};

/**
 * Tracked modal IDs mapped to their GTM popin_title value.
 * Only modals listed here trigger popin_view/popin_close events.
 */
export const TRACKED_MODALS: Record<string, string> = {
  newsletter: 'newsletter',
  engraving: 'engraving',
  contact: 'contact',
  'send-gift': '4gift',
  share: 'share',
  'cms-popup': 'cms-popup',
  'availability-request': 'availability request',
};
